import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

export interface RailSection {
  id: string;
  label: string;
  /** Count of configured entries; 0/undefined renders a hollow "empty" dot. */
  count?: number;
}

/**
 * Sticky in-page nav for the config sections. Each row shows a status dot —
 * solid when the section has configuration, hollow when empty — and a mono
 * count. Scrollspy highlights the section currently in view.
 */
export function SectionRail({ sections }: { sections: RailSection[] }) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Prefer the top-most visible section.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-10% 0px -70% 0px" },
    );
    sections.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => { observer.disconnect(); };
  }, [sections]);

  return (
    <nav className="sticky top-6 w-52 shrink-0 self-start max-lg:hidden">
      <p className="text-muted-foreground/70 mb-2 px-3 text-[10px] font-semibold tracking-widest uppercase">
        Sections
      </p>
      <ul className="border-border/60 space-y-0.5 border-l">
        {sections.map((s) => {
          const configured = (s.count ?? 0) > 0;
          const isActive = active === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => {
                  document
                    .getElementById(s.id)
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className={cn(
                  "-ml-px flex w-full items-center gap-2.5 border-l-2 py-1.5 pr-2 pl-3 text-left text-sm transition-colors",
                  isActive
                    ? "border-primary text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground border-transparent",
                )}
              >
                <span
                  className={cn(
                    "h-1.5 w-1.5 shrink-0 rounded-full transition-colors",
                    configured
                      ? "bg-emerald-500"
                      : "border-muted-foreground/40 border bg-transparent",
                  )}
                />
                <span className="min-w-0 flex-1 truncate">{s.label}</span>
                {configured && (
                  <span className="text-muted-foreground/70 font-mono text-[10px] tabular-nums">
                    {s.count}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
